import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  CheckCircle2, AlertCircle, XCircle, Info,
  ChevronDown, ChevronUp, ArrowRight, Microscope,
  FlaskConical, Droplets, ArrowDown, Gauge,
  Layers, Wind, Beaker, Zap, Cog, Database,
  ExternalLink
} from 'lucide-react';
import { generateProcessTrainValidation, type ValidationStep, type SampleData } from '@/lib/processTrainValidation';

const EQUIPMENT_ICONS: Record<string, React.ElementType> = {
  daf: Droplets, lamella: Layers, gac: Zap, multimedia: Layers,
  eq: Gauge, buffer: Gauge, aeration: Wind, secondary: Beaker,
  ph: FlaskConical, sludge: ArrowDown, screening: Cog,
};

const CAT_COLORS: Record<string, { border: string; bg: string; text: string; badge: string }> = {
  pretreatment: { border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', text: 'text-cyan-400', badge: 'bg-cyan-500/20 text-cyan-400' },
  primary: { border: 'border-amber-500/30', bg: 'bg-amber-500/10', text: 'text-amber-400', badge: 'bg-amber-500/20 text-amber-400' },
  secondary: { border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-400', badge: 'bg-emerald-500/20 text-emerald-400' },
  tertiary: { border: 'border-sky-500/30', bg: 'bg-sky-500/10', text: 'text-sky-400', badge: 'bg-sky-500/20 text-sky-400' },
  sludge: { border: 'border-rose-500/30', bg: 'bg-rose-500/10', text: 'text-rose-400', badge: 'bg-rose-500/20 text-rose-400' },
};

const STATUS_CONFIG = {
  pass: { icon: CheckCircle2, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', label: 'PASS' },
  warn: { icon: AlertCircle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', label: 'WARN' },
  fail: { icon: XCircle, color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20', label: 'FAIL' },
  info: { icon: Info, color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/20', label: 'INFO' },
};

interface Props {
  equipmentKeys: string[];
  sample: Partial<Record<string, number>>;
  trainName: string;
  onClose?: () => void;
}

function StepCard({ step, isOpen, onToggle }: { step: ValidationStep; isOpen: boolean; onToggle: () => void }) {
  const sc = STATUS_CONFIG[step.status] || STATUS_CONFIG.info;
  const StatusIcon = sc.icon;
  const EqIcon = EQUIPMENT_ICONS[step.equipmentKey] || Database;
  const catColors = CAT_COLORS[step.category] || CAT_COLORS.primary;

  return (
    <div className={`rounded-xl border overflow-hidden transition-all ${sc.border} ${isOpen ? sc.bg : 'bg-rb-surface'}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-3 hover:bg-rb-elevated/5 transition-colors text-left">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${catColors.bg} ${catColors.border} border`}>
          <EqIcon className={`w-3.5 h-3.5 ${catColors.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-slate-500">Step {step.stepNumber}</span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${sc.bg} ${sc.color} border ${sc.border}`}>
              {sc.label}
            </span>
            {step.reference && (
              <span className="text-[10px] text-slate-600">{step.reference}</span>
            )}
          </div>
          <p className="text-xs font-bold text-white truncate">{step.stepName}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-xs font-bold ${sc.color}`}>{step.result} {step.unit}</span>
          <StatusIcon className={`w-4 h-4 ${sc.color}`} />
          {isOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </div>
      </button>

      {isOpen && (
        <div className="px-3 pb-3 space-y-2 border-t border-slate-800/50">
          <p className="text-[11px] text-slate-400 mt-2">{step.explanation}</p>
          <div className="p-2.5 space-y-1.5 rb-inner-card-sm">
            <div>
              <span className="text-[10px] text-slate-500 uppercase font-bold">Formula</span>
              <p className="font-mono text-[11px] text-slate-500">{step.formula}</p>
            </div>
            <div>
              <span className="text-[10px] text-slate-500 uppercase font-bold">Substitution</span>
              <p className="font-mono text-[11px] text-sky-400">{step.substitution}</p>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <span className="text-[10px] text-slate-500 uppercase font-bold">Result:</span>
              <span className={`text-sm font-bold ${sc.color}`}>{step.result} {step.unit}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ProcessTrainValidationReport({ equipmentKeys, sample, trainName, onClose }: Props) {
  const navigate = useNavigate();
  const [openStepId, setOpenStepId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'pass' | 'warn' | 'fail' | 'info'>('all');
  const [filterEquipment, setFilterEquipment] = useState<string>('all');

  const steps = useMemo(() => generateProcessTrainValidation(equipmentKeys, sample), [equipmentKeys, sample]);

  const filteredSteps = useMemo(() => {
    return steps.filter((s) => {
      if (filterStatus !== 'all' && s.status !== filterStatus) return false;
      if (filterEquipment !== 'all' && s.equipmentKey !== filterEquipment) return false;
      return true;
    });
  }, [steps, filterStatus, filterEquipment]);

  const stats = useMemo(() => {
    return {
      total: steps.length,
      pass: steps.filter((s) => s.status === 'pass').length,
      warn: steps.filter((s) => s.status === 'warn').length,
      fail: steps.filter((s) => s.status === 'fail').length,
      info: steps.filter((s) => s.status === 'info').length,
    };
  }, [steps]);

  const uniqueEquipment = useMemo(() => {
    const map = new Map<string, { name: string; category: string }>();
    steps.forEach((s) => map.set(s.equipmentKey, { name: s.equipmentName, category: s.category }));
    return Array.from(map.entries());
  }, [steps]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Microscope className="w-5 h-5 text-sky-400" />
            Full Validation Report: {trainName}
          </h2>
          <p className="text-xs text-slate-500">
            {stats.total} steps across {uniqueEquipment.length} equipment units
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/equipment-validation')}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-sky-500/10 text-sky-400 text-xs border border-sky-500/20 hover:bg-sky-500/20 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open in Equipment Validation Hub
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg bg-slate-800 text-slate-400 text-xs hover:bg-slate-700 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-5 gap-2">
        {[
          { label: 'Total', value: stats.total, color: 'text-white', bg: 'bg-slate-800' },
          { label: 'Pass', value: stats.pass, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
          { label: 'Warn', value: stats.warn, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
          { label: 'Fail', value: stats.fail, color: 'text-rose-400', bg: 'bg-rose-500/10 border-rose-500/20' },
          { label: 'Info', value: stats.info, color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/20' },
        ].map((s) => (
          <div key={s.label} className={`rounded-lg p-2 text-center border ${s.bg}`}>
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] text-slate-500 uppercase">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] text-slate-500 uppercase font-bold">Filter:</span>
        {(['all', 'pass', 'warn', 'fail', 'info'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all ${
              filterStatus === s
                ? (s === 'all'
                    ? 'bg-slate-600/40 text-slate-100 border border-slate-500'
                    : STATUS_CONFIG[s].bg + ' ' + STATUS_CONFIG[s].color + ' border ' + STATUS_CONFIG[s].border)
                : 'text-slate-500 hover:text-slate-300 bg-slate-800/50'
            }`}
          >
            {s === 'all' ? 'All' : STATUS_CONFIG[s].label}
          </button>
        ))}
        <span className="text-[10px] text-slate-500 uppercase font-bold ml-3">Equipment:</span>
        <select
          value={filterEquipment}
          onChange={(e) => setFilterEquipment(e.target.value)}
          className="px-2 py-1 rounded-lg bg-slate-800 border border-slate-700 text-[10px] text-white focus:outline-none"
        >
          <option value="all">All</option>
          {uniqueEquipment.map(([key, info]) => (
            <option key={key} value={key}>{info.name}</option>
          ))}
        </select>
      </div>

      {/* Step List */}
      <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
        {filteredSteps.length === 0 ? (
          <div className="text-center py-8 text-xs text-slate-500">
            No steps match the selected filters.
          </div>
        ) : (
          filteredSteps.map((step) => (
            <StepCard
              key={`${step.equipmentKey}-${step.stepNumber}`}
              step={step}
              isOpen={openStepId === `${step.equipmentKey}-${step.stepNumber}`}
              onToggle={() => setOpenStepId(
                openStepId === `${step.equipmentKey}-${step.stepNumber}`
                  ? null
                  : `${step.equipmentKey}-${step.stepNumber}`
              )}
            />
          ))
        )}
      </div>
    </div>
  );
}
